import { useState } from 'react';
import { errorMessage } from '../api/client';
import {
  workstationsApi,
  type WorkstationCreateForm,
  type WorkstationForm,
} from '../api/endpoints';
import type { Workstation, WorkstationDetail } from '../api/types';
import {
  WORKSTATION_STATES,
  WORKSTATION_STATE_LABELS,
  WORKSTATION_TYPES,
  WORKSTATION_TYPE_LABELS,
  type WorkstationState,
} from '../domain/workstations';
import { formatDateOnly } from '../lib/format';
import { Field } from './Field';
import { Modal } from './Modal';
import { Notice } from './Notice';

const FORM_ID = 'formulaire-poste';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptyIdentity(): WorkstationForm {
  return {
    name: '',
    brand: '',
    model: '',
    serialNumber: null,
    type: 'fixe',
    commissionedOn: null,
    note: '',
  };
}

function identityOf(workstation: Workstation): WorkstationForm {
  return {
    name: workstation.name,
    brand: workstation.brand,
    model: workstation.model,
    serialNumber: workstation.serialNumber,
    type: workstation.type,
    commissionedOn: workstation.commissionedOn,
    note: workstation.note,
  };
}

/**
 * Champs d'identité, communs à la création et à la modification. À la
 * création, la date de mise en service est sortie de ce bloc : elle date
 * l'état initial et se saisit avec lui.
 */
function IdentityFields({
  form,
  patch,
  withCommissioning = true,
}: {
  form: WorkstationForm;
  patch: (change: Partial<WorkstationForm>) => void;
  withCommissioning?: boolean;
}) {
  return (
    <>
      <Field label="Nom interne" hint="Unique dans le parc.">
        <input
          value={form.name}
          required
          maxLength={120}
          onChange={(event) => patch({ name: event.target.value })}
        />
      </Field>

      <Field label="Marque">
        <input
          value={form.brand}
          required
          maxLength={120}
          onChange={(event) => patch({ brand: event.target.value })}
        />
      </Field>

      <Field label="Modèle">
        <input
          value={form.model}
          required
          maxLength={120}
          onChange={(event) => patch({ model: event.target.value })}
        />
      </Field>

      <Field label="Numéro de série" hint="Facultatif.">
        <input
          value={form.serialNumber ?? ''}
          maxLength={120}
          onChange={(event) =>
            patch({ serialNumber: event.target.value === '' ? null : event.target.value })
          }
        />
      </Field>

      <Field label="Type">
        <select
          value={form.type}
          onChange={(event) =>
            patch({ type: event.target.value as WorkstationForm['type'] })
          }
        >
          {WORKSTATION_TYPES.map((type) => (
            <option key={type} value={type}>
              {WORKSTATION_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </Field>

      {withCommissioning && (
        <Field label="Date de mise en service" hint="Facultatif.">
          <input
            type="date"
            value={form.commissionedOn ?? ''}
            onChange={(event) =>
              patch({
                commissionedOn: event.target.value === '' ? null : event.target.value,
              })
            }
          />
        </Field>
      )}

      <Field label="Note" wide>
        <textarea
          value={form.note}
          maxLength={4000}
          onChange={(event) => patch({ note: event.target.value })}
        />
      </Field>
    </>
  );
}

function Frame({
  title,
  busy,
  error,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id={FORM_ID} className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}
        <div className="grille-champs">{children}</div>
      </form>
    </Modal>
  );
}

export function WorkstationCreateModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (saved: WorkstationDetail) => void;
}) {
  const [form, setForm] = useState<WorkstationForm>(emptyIdentity);
  const [state, setState] = useState<WorkstationState>('enStock');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patch = (change: Partial<WorkstationForm>): void =>
    setForm((current) => ({ ...current, ...change }));

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const payload: WorkstationCreateForm = { ...form, state };
    workstationsApi
      .create(payload)
      .then(({ workstation }) => onSaved(workstation))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Frame
      title="Nouveau poste"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <IdentityFields form={form} patch={patch} withCommissioning={false} />

      <Field
        label="Date de mise en service"
        hint="Elle date l'état initial. À renseigner pour reprendre un poste déjà en place."
      >
        <input
          type="date"
          value={form.commissionedOn ?? ''}
          max={today()}
          onChange={(event) =>
            patch({
              commissionedOn: event.target.value === '' ? null : event.target.value,
            })
          }
        />
      </Field>

      <Field label="État initial">
        <select
          value={state}
          onChange={(event) => setState(event.target.value as WorkstationState)}
        >
          {WORKSTATION_STATES.map((value) => (
            <option key={value} value={value}>
              {WORKSTATION_STATE_LABELS[value]}
            </option>
          ))}
        </select>
      </Field>

      <div className="champ--large">
        <Notice tone="info">
          Le premier événement d'état du poste sera daté du{' '}
          <strong>
            {form.commissionedOn === null
              ? "jour de sa création, aujourd'hui"
              : formatDateOnly(form.commissionedOn)}
          </strong>
          . Un événement saisi ensuite avec une date antérieure décrira son passé
          sans devenir son état courant.
        </Notice>
      </div>
    </Frame>
  );
}

/**
 * Modification de l'identité seule. L'état ne se corrige pas ici : il
 * s'enregistre par un événement de changement d'état, comme le propriétaire.
 */
export function WorkstationUpdateModal({
  workstation,
  onClose,
  onSaved,
}: {
  workstation: Workstation;
  onClose: () => void;
  onSaved: (saved: WorkstationDetail) => void;
}) {
  const [form, setForm] = useState<WorkstationForm>(() => identityOf(workstation));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patch = (change: Partial<WorkstationForm>): void =>
    setForm((current) => ({ ...current, ...change }));

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    workstationsApi
      .update(workstation.id, form)
      .then(({ workstation: saved }) => onSaved(saved))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Frame
      title="Modifier le poste"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <IdentityFields form={form} patch={patch} />
    </Frame>
  );
}
